import type { LocationQuery } from 'vue-router';

import { tryOnScopeDispose, tryOnUnmounted } from '@vueuse/core';
import snakeCase from 'lodash/snakeCase';
import { computed, nextTick, readonly, ref } from 'vue';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { useTrackingStore } from '@/stores/tracking';
import { useUtmStore } from '@/stores/utm';

type BeforeBrandSwitchGuard = () => boolean | Promise<boolean>;

const copyQueryParams = ref(false);
const includeQueryParams = ref<string[]>([]);
const excludeQueryParams = ref<string[]>([]);
const isBrandSwitching = ref(false);
const beforeBrandSwitchGuards = new Set<BeforeBrandSwitchGuard>();

/**
 * Raised by the router guard while a navigation to a brand-prefixed URL of a
 * different brand is in flight (e.g. a Vision AI redirect widget). Brand
 * watchers that react with their own navigations check `isBrandSwitching` and
 * stay quiet, so they don't cancel the in-flight navigation.
 */
export function setNavigationBrandSwitch(value: boolean) {
  isBrandSwitching.value = value;
}

const includeQueryParamsToCheck = computed(() =>
  includeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

const excludeQueryParamsToCheck = computed(() =>
  excludeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

function enableCopyQueryParams({
  include,
  exclude,
}: { include?: string[]; exclude?: string[] } = {}) {
  copyQueryParams.value = true;
  includeQueryParams.value = Array.isArray(include) ? include : [];
  excludeQueryParams.value = Array.isArray(exclude) ? exclude : [];
}

function filterQueryParams(query: LocationQuery = {}) {
  if (!copyQueryParams.value) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(query).filter(
      ([param]) =>
        (!includeQueryParamsToCheck.value?.length ||
          includeQueryParamsToCheck.value.includes(param)) &&
        !(excludeQueryParamsToCheck.value ?? []).includes(param),
    ),
  );
}

async function runBeforeBrandSwitchGuards(): Promise<boolean> {
  for (const guard of beforeBrandSwitchGuards) {
    if (!(await guard())) return false;
  }
  return true;
}

function registerBeforeBrandSwitch(guard: BeforeBrandSwitchGuard): () => void {
  beforeBrandSwitchGuards.add(guard);
  const unregister = () => beforeBrandSwitchGuards.delete(guard);
  tryOnScopeDispose(unregister);
  return unregister;
}

export function useBrandSwitcher() {
  const route = useRoute();
  const router = useRouter();
  const utmStore = useUtmStore();
  const authStore = useAuthStore();
  const trackingStore = useTrackingStore();

  async function performBrandSwitch(brandLabel: string, query?: LocationQuery) {
    isBrandSwitching.value = true;

    try {
      const result = await router.push({
        name: route.name,
        params: { brandLabel },
        query,
      });

      if (!isNavigationFailure(result)) {
        void authStore.setCurrentBrand({ brandLabel });
      }

      return result;
    } finally {
      isBrandSwitching.value = false;
    }
  }

  async function switchBrand(brandLabel: string) {
    if (!(await runBeforeBrandSwitchGuards())) {
      return;
    }

    if (route?.params?.brandLabel) {
      const query = filterQueryParams(route.query);

      if (utmStore.isManagementSettingsDirty) {
        void utmStore.confirmLeaving(async () => {
          await performBrandSwitch(brandLabel, query);
        });
      } else {
        await performBrandSwitch(brandLabel, query);
      }
    } else {
      void authStore.setCurrentBrand({ brandLabel });
      void nextTick(() => {
        // faking a Page Viewed event to keep metrics consistent
        trackingStore.trackPageViewedEvent(route, null);
      });
    }
  }

  tryOnUnmounted(() => {
    copyQueryParams.value = false;
    includeQueryParams.value = [];
    excludeQueryParams.value = [];
  });

  return {
    isBrandSwitching: readonly(isBrandSwitching),
    enableCopyQueryParams,
    filterQueryParams,
    registerBeforeBrandSwitch,
    switchBrand,
  };
}
